import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import YearTopicCard from "@/components/YearTopicCard";
import TopicButton from "@/components/TopicButton";
import DifficultySelector, { GradeRange, getAvailableGrades } from "@/components/DifficultySelector";
import ModeSelector from "@/components/ModeSelector";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { storage } from "@/lib/storage";
import { ArrowLeft, BarChart3, BookOpen, Flame, History, Plus, GraduationCap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { Topic, YearLevelSummary } from "@shared/schema";
import { useProfile } from "@/hooks/use-profile";
import { useAllModuleStatuses } from "@/hooks/use-module-status";
import { useRolePerspective } from "@/hooks/use-role-perspective";
import { useSubject } from "@/contexts/SubjectContext";
import { getYearCategory, CATEGORY_ORDER, isSubjectMatch } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
export default function Home() {
  const { user, profile, loading: profileLoading } = useProfile();
  const { effectiveRole } = useRolePerspective(profile?.id, profile?.role);
  const [, setLocation] = useLocation();
  const [homeView, setHomeView] = useState<'landing' | 'quiz'>('landing');
  const [selectedYear, setSelectedYear] = useState<string | number | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<GradeRange[]>(['1-4', '5-6', '7-8', '9']);
  const [mode, setMode] = useState<'quiz' | 'study'>('quiz');
  const [streak, setStreak] = useState(0);
  const [allTopics, setAllTopics] = useState<Topic[]>([]);
  const [yearLevels, setYearLevels] = useState<YearLevelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const { statuses } = useAllModuleStatuses();

  const isQuizActive = statuses.quiz === 'active' || effectiveRole === 'admin';
  const isDrillActive = statuses.drill === 'active' || effectiveRole === 'admin';
  const statisticsStatus = statuses.statistics ?? 'active';
  const reviewStatus = statuses.review ?? 'active';
  const isStatisticsActive = statisticsStatus === 'active' || effectiveRole === 'admin';
  const isReviewActive = reviewStatus === 'active' || effectiveRole === 'admin';
  const quizStatusLabel = statuses.quiz === 'coming_soon' ? 'Coming Soon' : 'Maintenance';

  const { currentSubject, setCurrentSubject, subjects } = useSubject();

  useEffect(() => {
    if (!profileLoading && user) {
      if (profile && !profile.onboarding_completed) {
        setLocation('/onboarding');
      }
    }
  }, [user, profile, profileLoading, setLocation]);

  useEffect(() => {
    if (!currentSubject) return;
    
    const loadData = async () => {
      setLoading(true);
      try {
        const [topicsResponse, yearLevelsResponse] = await Promise.all([
          fetch(`/api/topics?subject=${currentSubject.id}`),
          fetch(`/api/year-levels?subject=${currentSubject.id}`),
        ]);

        if (topicsResponse.ok) {
          const data = await topicsResponse.json();
          setAllTopics(data);
        }

        if (yearLevelsResponse.ok) {
          const data = await yearLevelsResponse.json();
          setYearLevels(data);
        }
      } catch (error) {
        console.error('Failed to load topics:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();

    const prefs = storage.getPreferences();
    if (prefs.difficulty) {
      if (Array.isArray(prefs.difficulty)) {
        setDifficulty(prefs.difficulty as GradeRange[]);
      } else if (typeof prefs.difficulty === 'string') {
        setDifficulty(prefs.difficulty.split(",") as GradeRange[]);
      }
    } else {
      setDifficulty(['1-4', '5-6', '7-8', '9']);
    }
    setMode(prefs.studyMode ? 'study' : 'quiz');

    const progress = storage.getProgress();
    setStreak(progress.streak);
  }, [currentSubject]);

  const topics = selectedYear
    ? allTopics.filter(t => String(t.yearLevel) === String(selectedYear) && (!t.mode || t.mode === 'quiz') && isSubjectMatch(t, currentSubject.id))
    : [];
  const handleStartQuiz = () => {
    if (selectedTopic) {
      storage.savePreferences({ difficulty, studyMode: mode === 'study' });
      setLocation(`/quiz/${selectedTopic}?difficulty=${difficulty.join(",")}&mode=${mode}`);
    }
  };

  const selectedYearLabel = selectedYear
    ? yearLevels.find((level) => level.year === String(selectedYear))?.grade ||
    (String(selectedYear).match(/^\d+$/) ? `Year ${selectedYear}` : String(selectedYear))
    : "";

  const enterQuizMode = () => {
    setHomeView('quiz');
    setSelectedYear(null);
    setSelectedTopic(null);
  };

  const exitQuizMode = () => {
    setHomeView('landing');
    setSelectedYear(null);
    setSelectedTopic(null);
  };

  const renderQuizTools = () => (
    <div className="flex justify-center gap-2 flex-wrap">
      {statisticsStatus !== 'hidden' && (
        <div className="relative group">
          <Button
            variant="outline"
            onClick={() => isStatisticsActive && setLocation('/statistics')}
            data-testid="button-statistics"
            className={`gap-2 ${!isStatisticsActive ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!isStatisticsActive}
          >
            <BarChart3 className="h-4 w-4" />
            Statistics
          </Button>
          {!isStatisticsActive && (
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white text-xs py-1 px-2 rounded pointer-events-none whitespace-nowrap">
              {statisticsStatus === 'coming_soon' ? 'Coming Soon' : 'Maintenance'}
            </div>
          )}
        </div>
      )}

      {reviewStatus !== 'hidden' && (
        <div className="relative group">
          <Button
            variant="outline"
            onClick={() => isReviewActive && setLocation('/review')}
            data-testid="button-review"
            className={`gap-2 ${!isReviewActive ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!isReviewActive}
          >
            <History className="h-4 w-4" />
            Review
          </Button>
          {!isReviewActive && (
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white text-xs py-1 px-2 rounded pointer-events-none whitespace-nowrap">
              {reviewStatus === 'coming_soon' ? 'Coming Soon' : 'Maintenance'}
            </div>
          )}
        </div>
      )}

      {statuses.quiz !== 'hidden' && (
        <div className="relative group">
          <Button
            variant="outline"
            onClick={() => isQuizActive && setLocation('/custom-quiz')}
            data-testid="button-custom-quiz"
            className={`gap-2 ${!isQuizActive ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!isQuizActive}
          >
            <Plus className="h-4 w-4" />
            Custom Quiz
          </Button>
          {!isQuizActive && (
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white text-xs py-1 px-2 rounded pointer-events-none whitespace-nowrap">
              {statuses.quiz === 'coming_soon' ? 'Coming Soon' : 'Maintenance'}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderYearCards = () => {
    const groupedYears = yearLevels.reduce((acc, level) => {
      const category = getYearCategory(level.year);
      if (!acc[category]) acc[category] = [];
      acc[category].push(level);
      return acc;
    }, {} as Record<string, typeof yearLevels>);

    return (
      <Accordion type="multiple" className="w-full space-y-4">
        {CATEGORY_ORDER.filter(category => groupedYears[category] && groupedYears[category].length > 0).map(category => (
          <AccordionItem key={category} value={category} className="border bg-card rounded-lg px-4 shadow-sm">
            <AccordionTrigger className="hover:no-underline font-serif text-xl py-4">
              {category}
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2 pb-4">
                {statuses.quiz !== 'hidden' && groupedYears[category].map((level) => (
                  <div key={level.year} className="relative group">
                    <div className={!isQuizActive ? 'opacity-60 pointer-events-none' : ''}>
                      <YearTopicCard
                        yearLevel={level.year}
                        topicCount={level.topics}
                        gradeLevel={level.grade}
                        onClick={() => isQuizActive && level.topics > 0 && setSelectedYear(level.year)}
                      />
                    </div>
                    {!isQuizActive && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                        <span className="text-sm font-bold px-3 py-1 bg-background/90 border rounded-md shadow-sm uppercase tracking-widest text-muted-foreground">
                          {statuses.quiz === 'coming_soon' ? 'Coming Soon' : 'Maintenance'}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  };

  if (homeView === 'quiz' && selectedYear) {
    if (!currentSubject) return null;
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto space-y-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <Button
                variant="ghost"
                className="gap-2"
                onClick={() => {
                  setSelectedYear(null);
                  setSelectedTopic(null);
                }}
                data-testid="button-back"
              >
                <ArrowLeft className="h-5 w-5" />
                Back to Quiz Mode
              </Button>
              <div className="text-center flex-1">
                <h2 className="text-3xl font-bold font-serif">
                  {selectedYearLabel} Topics
                </h2>
                <p className="text-muted-foreground mt-1">
                  Select a topic and customize your quiz
                </p>
              </div>
              <div className="flex items-center gap-2">
                {streak > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    <Flame className="h-4 w-4 text-orange-500" />
                    {streak} day streak
                  </Badge>
                )}
              </div>
            </div>

            {renderQuizTools()}

            <div className="grid gap-3">
              {Object.entries(
                topics.reduce((acc, topic) => {
                  const cat = topic.category || "Uncategorized";
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(topic);
                  return acc;
                }, {} as Record<string, typeof topics>)
              ).sort(([a], [b]) => a.localeCompare(b)).map(([category, catTopics]) => (
                <div key={category} className="mb-6 space-y-3">
                  <h3 className="text-xl font-bold text-primary border-b pb-2">{category}</h3>
                  <div className="grid gap-3">
                    {catTopics.map((topic) => (
                      <TopicButton
                        key={topic.id}
                        name={topic.name}
                        isSelected={selectedTopic === topic.id}
                        onClick={() => setSelectedTopic(topic.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {selectedTopic && (
              <div className="space-y-6">
                <DifficultySelector
                  selected={difficulty}
                  availableGrades={getAvailableGrades(topics.find(t => t.id === selectedTopic)?.questions || [])}
                  onChange={setDifficulty}
                />
                <ModeSelector
                  selected={mode}
                  onChange={setMode}
                />
                <div className="flex justify-center pt-4">
                  <Button
                    size="lg"
                    onClick={handleStartQuiz}
                    className="w-full sm:w-auto"
                    data-testid="button-start-quiz"
                  >
                    Start {mode === 'quiz' ? 'Quiz' : 'Practice'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (homeView === 'quiz') {
    if (!currentSubject) return null;
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto space-y-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <Button
                variant="ghost"
                className="gap-2"
                onClick={exitQuizMode}
                data-testid="button-back-home"
              >
                <ArrowLeft className="h-5 w-5" />
                Back to Home
              </Button>
              <div className="text-center flex-1">
                <Badge variant="secondary" className="mx-auto mb-3 gap-1 px-3 py-1">
                  <BookOpen className="h-4 w-4" />
                  AddvanceLab - {currentSubject.label}
                </Badge>
                <div className="flex items-center justify-center gap-3">
                  <h2 className="text-4xl font-bold font-serif">
                    Choose Your Year Level
                  </h2>
                </div>
                <p className="text-lg text-muted-foreground mt-2">
                  Select your year to practice {currentSubject.label} topics
                </p>
              </div>
              <div className="flex items-center gap-2">
                {streak > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    <Flame className="h-4 w-4 text-orange-500" />
                    {streak} day streak
                  </Badge>
                )}
              </div>
            </div>

            {renderQuizTools()}

            {renderYearCards()}
          </div>
        </div>
      </div>
    );
  }

  if (!currentSubject) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto space-y-10">
          <div className="space-y-4 text-center pt-6">
            <h2 className="text-4xl md:text-5xl font-bold font-serif transition-colors">
              Choose how you want to learn {currentSubject.label}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Quiz Mode opens the topic library when you're ready. Drill and Teacher Dashboard stay one click away.
            </p>
            <div className="mx-auto mt-6 inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              Looking for a different subject? Use the selector in the top left corner of the navigation bar!
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3 items-stretch">
            {statuses.quiz !== 'hidden' && (
              <Card
                className={`lg:col-span-2 overflow-hidden border-2 transition-all ${isQuizActive ? 'cursor-pointer border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card shadow-sm hover:border-primary/40 hover:shadow-lg' : 'cursor-not-allowed border-border bg-muted/30 opacity-70'}`}
                onClick={() => {
                  if (isQuizActive) {
                    enterQuizMode();
                  }
                }}
                onKeyDown={(event) => {
                  if (!isQuizActive) {
                    return;
                  }

                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    enterQuizMode();
                  }
                }}
                role={isQuizActive ? 'button' : undefined}
                tabIndex={isQuizActive ? 0 : undefined}
                aria-disabled={!isQuizActive}
              >
                <div className="flex h-full flex-col justify-between gap-6 p-6 sm:p-8">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-background/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                        Quiz Mode
                      </div>
                      {!isQuizActive && (
                        <span className="text-xs font-semibold px-2 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-md uppercase tracking-wider">
                          {quizStatusLabel}
                        </span>
                      )}
                    </div>
                    <h3 className="text-3xl font-bold font-serif">
                      Browse topics only after you enter quiz mode
                    </h3>
                    <p className="max-w-2xl text-muted-foreground">
                      The topic grid stays out of the way until you choose it. Click this panel to open quiz mode, where you’ll also find Statistics, Review, and Custom Quiz.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div className="grid gap-4">
              {statuses.drill !== 'hidden' && (
                <Card
                  className={`p-6 transition-all border-2 ${isDrillActive ? 'cursor-pointer hover:shadow-lg hover:border-primary/50 group' : 'opacity-60 cursor-not-allowed bg-muted/30'}`}
                  onClick={() => {
                    if (isDrillActive) setLocation('/drills');
                  }}
                >
                  <div className="flex h-full flex-col gap-4">
                    <div className={`p-3 rounded-xl w-fit transition-colors ${isDrillActive ? 'bg-primary/10 group-hover:bg-primary/20' : 'bg-muted pb-0'}`}>
                      <Flame className={`h-8 w-8 ${isDrillActive ? 'text-orange-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 w-full">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold">Drills</h3>
                        {!isDrillActive && (
                          <span className="text-xs font-semibold px-2 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-md uppercase tracking-wider">
                            {statuses.drill === 'coming_soon' ? 'Coming Soon' : 'Maintenance'}
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground">
                        Quick-fire practice sets for a fast warm-up or revision session.
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {(!user || effectiveRole !== 'student') && (
                <Card
                  className="p-6 transition-all border-2 cursor-pointer hover:shadow-lg hover:border-primary/50 group"
                  onClick={() => setLocation('/teacher-dashboard')}
                >
                  <div className="flex h-full flex-col gap-4">
                    <div className="p-3 rounded-xl w-fit bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <GraduationCap className="h-8 w-8 text-primary" />
                    </div>
                    <div className="flex-1 w-full">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold">Teacher Dashboard</h3>
                        <span className="text-xs font-semibold px-2 py-1 bg-primary/10 text-primary rounded-md uppercase tracking-wider">
                          Open
                        </span>
                      </div>
                      <p className="text-muted-foreground">
                        Manage worksheets, starters, and classroom tools from one place.
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

